import React, { useEffect, useState } from "react";
import ProductGrowthTable from "../../../../utility/table/ProductGrowthTable.jsx";
import Breadcrumb from "../../../../utility/breadcrumb/Breadcrumb";
import usePersistentDateRange from "../../Common/usePersistentDateRange";
import useProductGrowth from "./useProductGrowth";
import ProductGrowthFilter from "./ProductGrowthFilter";
import PermissionControlledCard from "../../../../common/PermissionControlledCard.jsx";
import { usePermissions } from "../../../../context/PermissionContext.js";
import AllowedCard from "../../../../common/AllowedCard.jsx";
import DashboardReady from "../DashboardReady/DashboardReady.jsx";

const DEFAULT_PARAMS = {
  range: "23",
  range_type: "week",
  year: 2025,
  comparison: "net_sales",
  product_ids: [],
};

const ProductGrowth = () => {
  const {
    top10,
    low10,
    fastestGrowing,
    declining,
    heroes,
    hourlyPerformanceState,
    filterOptions,
    loading,
    fetchTop10,
    fetchLow10,
    fetchFastestGrowing,
    fetchDeclining,
    fetchHeroes,
    fetchHourlyPerformance, fetchFilterOption, accessDenied
  } = useProductGrowth();

  const { selectedRange, handleDateChange } = usePersistentDateRange();
  const dashboardReady = localStorage.getItem("dashboard-ready") === "true";
  const permission = usePermissions();
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const weekAllowed = permission?.data?.product_growth?.filters?.week;
  const monthAllowed = permission?.data?.product_growth?.filters?.month;
  const productsAllowed = permission?.data?.product_growth?.filters?.products;
  let selectedType = { ...JSON.parse(localStorage.getItem("productGrowthFilters")) } || { ...DEFAULT_PARAMS };

  const isNoPermission = !weekAllowed?.display && !monthAllowed?.display && !productsAllowed?.display;

  const applyFilters = (params) => {
    const finalParams = {
      ...params,
      range_type: params?.wise || params?.range_type, // week or month
      year: params.year,
      range: params.range
    };
    fetchTop10(finalParams);
    fetchLow10(finalParams);
    fetchFastestGrowing(finalParams);
    fetchDeclining(finalParams);
    fetchHeroes(finalParams);
    fetchHourlyPerformance(finalParams);
  };


  const downloadBtnPermission = permission?.data?.product_growth?.sections?.download_option?.display && permission?.data?.product_growth?.sections?.download_option?.allowed

  useEffect(() => {
    fetchFilterOption(DEFAULT_PARAMS);
  }, []);


  useEffect(() => {
    if (!permission?.data || !filterOptions) return;

    const week = permission.data.product_growth?.filters?.week;
    const month = permission.data.product_growth?.filters?.month;

    let updated = { ...JSON.parse(localStorage.getItem("productGrowthFilters")) } || { ...DEFAULT_PARAMS };
    let changed = false;

    // CASE 1: Week permission FALSE → force Month
    if (!week?.allowed) {
      if (updated?.wise === "week" || updated?.range_type === "week") {
        updated.wise = "month";
        updated.range_type = "month";
        updated.range = filterOptions?.months?.[0]?.value || "";
        changed = true;
      }
    }

    // CASE 2: Month permission FALSE → force Week
    if (!month?.allowed) {
      if (updated.wise === "month" || updated.range_type === "month") {
        updated.wise = "week";
        updated.range_type = "week";
        updated.range = filterOptions?.weeks?.[0]?.value || "";
        changed = true;
      }
    }

    if (changed) {
      localStorage.setItem("productGrowthFilters", JSON.stringify(updated));
    }

    applyFilters(updated);

  }, [permission, filterOptions]);


  // 🔹 Transform API response into UI-ready format
  const transformedData = React.useMemo(() => {
    if (!hourlyPerformanceState?.data) return { peakVsOff: { peak: {}, off: {}, hours: [] } };

    const raw = hourlyPerformanceState?.data;

    return {
      peakVsOff: {
        peak: {
          revenue: raw.peak_hours.net_sales_amount,
          orders: raw.peak_hours.actual_units,
          share: raw.peak_hours.percentage,
          hour: raw.peak_hours.hours
        },
        off: {
          revenue: raw.off_hours.net_sales_amount,
          orders: raw.off_hours.actual_units,
          share: raw.off_hours.percentage,
          hour: raw.off_hours.hours
        },
        hours: raw.summary.map((h) => ({
          hour: h.hour ?? "Other",
          type: h.type,
          value: h.percentage, // % height for bar
        })),
      },
    };
  }, [hourlyPerformanceState]);

  if (accessDenied) {
    return (
      <div className='d-flex justify-content-center' style={{ textAlign: "center", padding: "30px", fontWeight: "500" }}>
        <AllowedCard />
      </div>
    )
  }

  const isNoPermissionBox = () => {
    return (
      <div className="card mt-5">
        <div className="card-body h-auto">
          <div className="no-data p-5" style={{ textAlign: 'center', fontWeight: '500' }}>
            No data found
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className='without-datepicker-breadcrumb'>
        <Breadcrumb />
      </div>

      {!dashboardReady ? (
        <DashboardReady />
      ) : (
        <>
          {isNoPermission ? (
            isNoPermissionBox()
          ) : <>
            {filterOptions && (
              <ProductGrowthFilter defaultValueFilter={DEFAULT_PARAMS} filterOptions={filterOptions} onApply={applyFilters} />
            )}
            <section className="section dashboard py-3">
              <div className="pg-container-fluid">
                <main className="pg-grid gap-3">
                  {/* Top & Low 10 */}
                  <PermissionControlledCard permission={permission?.data?.product_growth?.sections?.top_10_skus} colClass="pg-card pg-span-6 md:pg-span-6">
                    <section className="pg-card pg-span-6 md:pg-span-6">
                      <div className="pg-content pg-table-wrap">
                        <ProductGrowthTable
                          tableId="top10"
                          data={top10?.data?.list}
                          enableSelection={false}
                          title="Top 10 SKUs"
                          range={top10?.data?.range}
                          loading={loading?.top10}
                          downloadBtnPermission={downloadBtnPermission}
                        />
                      </div>
                    </section>
                  </PermissionControlledCard>

                  <PermissionControlledCard permission={permission?.data?.product_growth?.sections?.low_10_skus} colClass="pg-card pg-span-6 md:pg-span-6">
                    <section className="pg-card pg-span-6 md:pg-span-6">
                      <div className="pg-content pg-table-wrap">
                        <ProductGrowthTable
                          tableId="low10"
                          data={low10?.data?.list}
                          enableSelection={false}
                          title="Low 10 SKUs"
                          range={low10?.data?.range}
                          loading={loading.low10}
                          downloadBtnPermission={downloadBtnPermission}
                        />
                      </div>
                    </section>
                  </PermissionControlledCard>

                  {/* Growth & Declining */}
                  <PermissionControlledCard permission={permission?.data?.product_growth?.sections?.fastest_growing_skus} colClass="pg-card pg-span-6 md:pg-span-6">
                    <section className="pg-card pg-span-6 md:pg-span-6">
                      <div className="pg-content pg-table-wrap">
                        <ProductGrowthTable
                          tableId="fastestGrowing"
                          data={fastestGrowing?.data?.list}
                          enableSelection={false}
                          title="Fastest Growing SKUs"
                          range={fastestGrowing?.data?.range}
                          loading={loading.fastestGrowing}
                          downloadBtnPermission={downloadBtnPermission}
                        />
                      </div>
                    </section>
                  </PermissionControlledCard>
                  <PermissionControlledCard permission={permission?.data?.product_growth?.sections?.declining_skus} colClass="pg-card pg-span-6 md:pg-span-6">
                    <section className="pg-card pg-span-6 md:pg-span-6">
                      <div className="pg-content pg-table-wrap">
                        <ProductGrowthTable
                          tableId="declining"
                          data={declining?.data?.list}
                          enableSelection={false}
                          title="Declining SKUs"
                          range={declining?.data?.range}
                          loading={loading.declining}
                          downloadBtnPermission={downloadBtnPermission}
                        />
                      </div>
                    </section>
                  </PermissionControlledCard>
                  {((selectedType.wise === "month" && heroes.length > 0) || (selectedType.wise === "week")) &&
                    <PermissionControlledCard permission={permission?.data?.product_growth?.sections?.streak_week} colClass="pg-card pg-span-12">
                      <section className="pg-card pg-span-12">
                        <div className="pg-content pg-table-wrap">
                          <ProductGrowthTable
                            tableId="heroes"
                            data={heroes?.data?.list}
                            enableSelection={false}
                            title="Hero Products (4-Week Positive Streak)"
                            range={heroes?.data?.range}
                            loading={loading.heroes}
                            downloadBtnPermission={downloadBtnPermission}
                          />
                        </div>
                      </section>
                    </PermissionControlledCard>
                  }

                  {/* Peak vs Off-Hour */}
                  <PermissionControlledCard permission={permission?.data?.product_growth?.sections?.peak_hour_vs_off_hour_performance} colClass="pg-card pg-span-12">
                    <section className="pg-card pg-span-12">
                      <div className="pg-content">
                        <h6 className="table-title m-0">Peak Hours vs Off Hours Performance</h6>
                        <div className="pg-bands">
                          <div className="pg-band">
                            <h6 className="table-title-inner m-0">Peak (Top Hours)</h6>
                            <div className="mb-3 mt-2" style={{ display: 'flex', alignItems: 'center', gap: '10px', overflowX: 'auto', whiteSpace: 'nowrap', width: "730px", padding: '5px', scrollbarWidth: 'none', msOverflowStyle: 'none', }}>
                              {transformedData && transformedData?.peakVsOff?.peak?.hour?.map((value, index) => (
                                <span key={index} className="pg-badge-mini">
                                  {value}
                                </span>
                              ))}
                            </div>

                            <div className="pg-bar">
                              <div
                                className="pg-fill"
                                style={{ width: `${transformedData.peakVsOff.peak.share || 0}%` }}
                              />
                            </div>
                            <div className="pg-kpis">
                              <span className="pg-pill">
                                <b>{transformedData.peakVsOff.peak.revenue}</b> Net Sales
                              </span>
                              <span className="pg-pill">
                                <b>{transformedData.peakVsOff.peak.orders}</b> Actual Units
                              </span>
                              {/*<span className="pg-pill">
                        <strong>{transformedData.peakVsOff.peak.aov}</strong> AOV
                      </span>*/}
                            </div>
                          </div>

                          <div className="pg-band">
                            <h6 className="table-title-inner m-0">Off-Hour (Other Hours)</h6>
                            <div className="mb-3 mt-2" style={{ display: 'flex', alignItems: 'center', gap: '10px', overflowX: 'auto', whiteSpace: 'nowrap', width: "730px", padding: '5px', scrollbarWidth: 'none', msOverflowStyle: 'none', }}>
                              {transformedData && transformedData?.peakVsOff?.off?.hour?.map((value, index) => (
                                <span key={index} className="pg-badge-mini">
                                  {value}
                                </span>
                              ))}
                            </div>

                            <div className="pg-bar pg-off">
                              <div
                                className="pg-fill"
                                style={{ width: `${transformedData.peakVsOff.off.share || 0}%` }}
                              />
                            </div>
                            <div className="pg-kpis">
                              <span className="pg-pill">
                                <b>{transformedData.peakVsOff.off.revenue}</b> Net Sales
                              </span>
                              <span className="pg-pill">
                                <b>{transformedData.peakVsOff.off.orders}</b> Actual Units
                              </span>
                              {/*<span className="pg-pill">
                        <strong>{transformedData.peakVsOff.off.aov}</strong> AOV
                      </span>*/}
                            </div>
                          </div>
                        </div>

                        {/* Hourly Performance */}
                        <div className="pg-hours">
                          {transformedData.peakVsOff.hours.map((h, i) => (
                            <div className="pg-hour" key={i}>
                              <div className="pg-meta">
                                <span className="pg-badge-mini">{h.hour}</span>
                                <span
                                  className={`pg-badge-mini ${h.type === "peak" ? "" : "pg-peak"}`}
                                >
                                  {h.type}
                                </span>
                              </div>
                              <div className={`pg-hbar ${h.type === "peak" ? "" : "pg-peak"}`}>
                                <div className="pg-hfill" style={{ height: `${h.value}%` }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </section>
                  </PermissionControlledCard>
                </main>
              </div>
            </section>
          </>}
        </>)
      }
    </>
  );
};

export default ProductGrowth;
